/**
 * Étape Stockage — questions détaillées + travaux ciblés.
 *
 * Distingue le stockage **opérationnel** (matériel, foin, intrants) du
 * stockage **commercial** (céréales, fruits, vin, lait) qui ouvre l'éligibilité
 * aux fiches de régulation froid (basse/haute pression flottante) et de
 * régulation ventilation silos.
 */

import { motion, AnimatePresence } from 'framer-motion';
import { useMemo } from 'react';
import {
  ArrowRight,
  CheckCircle2,
  Info,
  Snowflake,
  Sparkles,
  Warehouse,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { cn } from '../../../lib/cn';
import { easeStripe } from '../../../lib/motion';
import {
  STOCKAGE_USAGES,
  TRAVAUX_AGRI,
  type StockageUsage,
  type TravauxAgriOption,
} from './agri-config';
import { useAgriStore } from './useAgriStore';

export function Step3cStockage() {
  const stockage = useAgriStore((s) => s.stockage);
  const travaux = useAgriStore((s) => s.travaux);
  const patchStockage = useAgriStore((s) => s.patchStockage);
  const toggleTravail = useAgriStore((s) => s.toggleTravail);
  const goNext = useAgriStore((s) => s.goNext);

  const hasCereales = stockage.usages.includes('cereales-grains');
  const hasFoinPaille = stockage.usages.includes('foin-paille');
  const hasCommercial = stockage.usages.some((u) =>
    STOCKAGE_USAGES.find((m) => m.id === u && m.vocation === 'commercial'),
  );

  /* — Travaux stockage à afficher — */
  const stockageItems = useMemo(
    () => TRAVAUX_AGRI.filter((t) => t.category === 'stockage'),
    [],
  );

  /* — Travaux utilités froid si stockage réfrigéré commercial — */
  const froidItems = useMemo(() => {
    if (!stockage.refrigere) return [];
    return TRAVAUX_AGRI.filter(
      (t) =>
        t.category === 'utilites' &&
        (t.id === 'util-bp-flottante' || t.id === 'util-hp-flottante'),
    );
  }, [stockage.refrigere]);

  /* — Recommandations — */
  const recommendedIds = useMemo(() => {
    const r: string[] = [];
    if (hasCereales) r.push('stock-regulation-silo');
    if (hasFoinPaille) r.push('stock-sechage-solaire');
    if (stockage.refrigere) {
      r.push('util-bp-flottante', 'util-hp-flottante');
    }
    return Array.from(new Set(r));
  }, [hasCereales, hasFoinPaille, stockage.refrigere]);

  const minimumAnswered = stockage.usages.length > 0;

  return (
    <div>
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
          <Warehouse className="h-3.5 w-3.5" strokeWidth={2} />
        </span>
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-brand-700">
          Section stockage · questions détaillées
        </p>
      </div>
      <h2 className="mt-2 h-section">
        Parlons de votre stockage
      </h2>
      <p className="mt-2 text-[14px] leading-relaxed text-stone-500">
        Distinguons ce que vous stockez{' '}
        <strong className="text-ink">pour votre activité</strong> (matériel,
        intrants) de ce que vous stockez{' '}
        <strong className="text-ink">à revendre</strong> (céréales, fruits,
        vin…). Les fiches CEE diffèrent selon les usages.
      </p>

      {/* ── Usages — par vocation ─────────────────────────────── */}
      <SubField label="Que stockez-vous ? (sélection multiple)">
        <div className="space-y-4">
          <UsageGroup
            title="Pour l'activité — opérationnel"
            vocation="operationnel"
            values={stockage.usages}
            onChange={(v) => patchStockage({ usages: v })}
          />
          <UsageGroup
            title="À revendre — commercial"
            vocation="commercial"
            values={stockage.usages}
            onChange={(v) => patchStockage({ usages: v })}
          />
        </div>
      </SubField>

      {/* ── Réfrigéré ─────────────────────────────────────────── */}
      <SubField label="Stockage réfrigéré ?">
        <div className="flex gap-2">
          <BigYesNo
            label="Oui, réfrigéré"
            selected={stockage.refrigere === true}
            onClick={() => patchStockage({ refrigere: true })}
          />
          <BigYesNo
            label="Non, ambiance"
            selected={stockage.refrigere === false}
            onClick={() => patchStockage({ refrigere: false })}
          />
        </div>
        {hasCommercial && stockage.refrigere === null && (
          <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-amber-700">
            <Snowflake className="h-3 w-3" strokeWidth={2} />
            Souvent réfrigéré pour les produits commerciaux (fruits, vin, lait).
          </p>
        )}
      </SubField>

      {/* ── Surface ───────────────────────────────────────────── */}
      <SubField label="Surface totale de stockage (m²)">
        <Input
          type="text"
          inputMode="numeric"
          placeholder="Ex : 400"
          value={
            stockage.surfaceM2 != null
              ? stockage.surfaceM2.toLocaleString('fr-FR')
              : ''
          }
          onChange={(e) => {
            const raw = e.target.value.replace(/\D/g, '');
            patchStockage({ surfaceM2: raw === '' ? null : parseInt(raw, 10) });
          }}
          className="h-12 rounded-xl border-2 border-stone-200 text-[15px] font-medium tabular-nums focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30"
        />
      </SubField>

      {/* ── Travaux stockage suggérés ──────────────────────────── */}
      <div className="mt-7">
        <div className="flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-accent-700" strokeWidth={2.2} />
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-accent-900">
            Travaux CEE stockage applicables
          </p>
        </div>
        <p className="mt-1 text-[12.5px] leading-relaxed text-stone-500">
          Sélectionnez ce qui vous intéresse — les recommandations s'adaptent
          à vos usages.
        </p>

        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {stockageItems.map((item) => (
            <TravailCard
              key={item.id}
              item={item}
              selected={travaux.includes(item.id)}
              recommended={recommendedIds.includes(item.id)}
              onToggle={toggleTravail}
            />
          ))}
        </div>
      </div>

      {/* ── Travaux froid révélés si réfrigéré ─────────────────── */}
      <AnimatePresence>
        {froidItems.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: easeStripe }}
            className="overflow-hidden"
          >
            <div className="mt-5">
              <div className="flex items-center gap-2">
                <Snowflake
                  className="h-3.5 w-3.5 text-accent-700"
                  strokeWidth={2.2}
                />
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-accent-900">
                  Puisque votre stockage est réfrigéré…
                </p>
              </div>
              <p className="mt-1 text-[12.5px] leading-relaxed text-stone-500">
                Optimisez la performance de vos groupes de froid.
              </p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {froidItems.map((item) => (
                  <TravailCard
                    key={item.id}
                    item={item}
                    selected={travaux.includes(item.id)}
                    recommended={recommendedIds.includes(item.id)}
                    onToggle={toggleTravail}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Button
        variant="brand"
        size="xl"
        disabled={!minimumAnswered}
        onClick={goNext}
        className="mt-7 w-full gap-2"
      >
        Continuer
        <ArrowRight className="h-4 w-4" />
      </Button>

      <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-stone-400">
        <Info className="h-3 w-3" strokeWidth={1.8} />
        Vous pourrez ajouter d'autres travaux à l'étape "Récapitulatif"
      </p>
    </div>
  );
}

/* ─── Helpers UI ─────────────────────────────────────────── */

function SubField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-5">
      <Label className="text-[12.5px] font-semibold text-ink">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function BigYesNo({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'flex h-11 flex-1 items-center justify-center gap-1.5 rounded-xl border-2 text-[13.5px] font-semibold transition-all duration-150',
        selected
          ? 'border-brand-500 bg-brand-50 text-brand-800'
          : 'border-stone-200 bg-white text-stone-600 hover:border-stone-300',
      )}
    >
      {selected && <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.4} />}
      {label}
    </button>
  );
}

function UsageGroup({
  title,
  vocation,
  values,
  onChange,
}: {
  title: string;
  vocation: 'commercial' | 'operationnel';
  values: StockageUsage[];
  onChange: (v: StockageUsage[]) => void;
}) {
  const options = STOCKAGE_USAGES.filter((u) => u.vocation === vocation);
  const isCommercial = vocation === 'commercial';

  function toggle(id: StockageUsage) {
    if (values.includes(id)) onChange(values.filter((v) => v !== id));
    else onChange([...values, id]);
  }

  return (
    <div>
      <p
        className={cn(
          'mb-1.5 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider',
          isCommercial
            ? 'bg-accent-100 text-accent-900'
            : 'bg-stone-100 text-stone-600',
        )}
      >
        {title}
      </p>
      <div className="grid grid-cols-2 gap-2">
        {options.map((opt) => {
          const Icon = opt.icon;
          const selected = values.includes(opt.id);
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => toggle(opt.id)}
              className={cn(
                'flex items-start gap-2.5 rounded-xl border-2 p-3 text-left transition-all duration-150',
                selected
                  ? 'border-brand-500 bg-brand-50 shadow-[0_2px_8px_-2px_rgba(16,185,129,0.2)]'
                  : 'border-stone-200 bg-white hover:border-stone-300 hover:bg-stone-50',
              )}
            >
              <span
                className={cn(
                  'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border-2',
                  selected
                    ? 'border-brand-500 bg-brand-500'
                    : 'border-stone-300 bg-white',
                )}
              >
                {selected && (
                  <svg
                    className="h-2.5 w-2.5 text-white"
                    viewBox="0 0 12 10"
                    fill="none"
                  >
                    <path
                      d="M1 5l3.5 3.5L11 1"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </span>
              <Icon
                className={cn(
                  'mt-0.5 h-3.5 w-3.5 shrink-0',
                  selected ? 'text-brand-700' : 'text-stone-500',
                )}
                strokeWidth={1.8}
              />
              <div className="min-w-0 flex-1">
                <p
                  className={cn(
                    'text-[12.5px] font-semibold leading-tight',
                    selected ? 'text-brand-900' : 'text-ink',
                  )}
                >
                  {opt.label}
                </p>
                <p className="mt-0.5 truncate text-[10.5px] text-stone-500">
                  {opt.sublabel}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TravailCard({
  item,
  selected,
  recommended,
  onToggle,
}: {
  item: TravauxAgriOption;
  selected: boolean;
  recommended: boolean;
  onToggle: (id: string) => void;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      onClick={() => onToggle(item.id)}
      className={cn(
        'group relative flex items-start gap-3 overflow-hidden rounded-xl border-2 p-3.5 text-left transition-all duration-150',
        selected
          ? 'border-brand-500 bg-brand-50/80 shadow-[0_4px_20px_-8px_rgba(16,185,129,0.25)]'
          : recommended
            ? 'border-accent-300 bg-accent-50/30 hover:border-accent-400'
            : 'border-stone-200 bg-white hover:border-stone-300 hover:bg-stone-50/40',
      )}
    >
      {recommended && !selected && (
        <span className="absolute right-1.5 top-1.5 inline-flex items-center gap-0.5 rounded-md bg-accent-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-accent-900">
          <Sparkles className="h-2.5 w-2.5" strokeWidth={2.4} />
          Reco
        </span>
      )}

      <span
        className={cn(
          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2',
          selected
            ? 'border-brand-500 bg-brand-500'
            : 'border-stone-300 bg-white group-hover:border-stone-400',
        )}
      >
        <AnimatePresence>
          {selected && (
            <motion.span
              initial={{ opacity: 0, scale: 0.4 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.4 }}
              transition={{ duration: 0.15, ease: easeStripe }}
            >
              <CheckCircle2 className="h-3 w-3 text-white" strokeWidth={3} />
            </motion.span>
          )}
        </AnimatePresence>
      </span>

      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'text-[13.5px] font-semibold leading-snug',
            selected ? 'text-brand-900' : 'text-ink',
          )}
        >
          {item.label}
        </p>
        <p className="mt-0.5 text-[11.5px] leading-relaxed text-stone-500">
          {item.sublabel}
        </p>
        <div className="mt-2 flex flex-wrap gap-1">
          {item.ceeCodes.map((code) => (
            <span
              key={code}
              className={cn(
                'inline-flex items-center rounded-md px-1.5 py-0.5 font-mono text-[9.5px] font-bold tracking-wider ring-1 ring-inset',
                selected
                  ? 'bg-accent-100 text-accent-900 ring-accent-200/60'
                  : 'bg-stone-100 text-stone-600 ring-stone-200/70',
              )}
            >
              {code}
            </span>
          ))}
        </div>
      </div>
    </button>
  );
}
